/**
 * Ingress management API — content needs, entity groups, priority schedules.
 *
 * Backend routes: /admin/ingress/needs, /admin/ingress/groups,
 * /admin/ingress/priorities, /admin/ingress/sources/{name}/fetch,
 * /admin/ingress/stats
 */

import { apiClient } from './config.js';

// ============================================================================
// Content Needs
// ============================================================================

export interface ContentNeed {
  need_id: string;
  need_name: string;
  need_type: string;
  match_criteria: Record<string, any>;
  source_preferences: Record<string, any>;
  date_mode: string;
  starts_at: string | null;
  expires_at: string | null;
  priority: number;
  daily_limit: number | null;
  total_limit: number | null;
  items_fulfilled: number;
  is_active: boolean;
  schedule_config: NeedScheduleConfig;
  last_fetched_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface ContentNeedCreate {
  need_name: string;
  need_type: string;
  match_criteria?: Record<string, any>;
  source_preferences?: Record<string, any>;
  date_mode?: string;
  starts_at?: string | null;
  expires_at?: string | null;
  priority?: number;
  daily_limit?: number | null;
  total_limit?: number | null;
  schedule_config?: NeedScheduleConfig;
}

export interface ContentNeedUpdate {
  need_name?: string;
  match_criteria?: Record<string, any>;
  source_preferences?: Record<string, any>;
  date_mode?: string;
  starts_at?: string | null;
  expires_at?: string | null;
  priority?: number;
  daily_limit?: number | null;
  total_limit?: number | null;
  is_active?: boolean;
  schedule_config?: NeedScheduleConfig;
}

export async function listContentNeeds(
  activeOnly: boolean = true,
  needType?: string,
): Promise<ContentNeed[]> {
  const params: Record<string, any> = { active_only: activeOnly };
  if (needType) params.need_type = needType;
  const { data } = await apiClient.get('/admin/ingress/needs', { params });
  return data.needs;
}

export async function getContentNeed(needId: string): Promise<ContentNeed> {
  const { data } = await apiClient.get(`/admin/ingress/needs/${needId}`);
  return data;
}

export async function createContentNeed(body: ContentNeedCreate): Promise<ContentNeed> {
  const { data } = await apiClient.post('/admin/ingress/needs', body);
  return data;
}

export async function updateContentNeed(needId: string, body: ContentNeedUpdate): Promise<ContentNeed> {
  const { data } = await apiClient.put(`/admin/ingress/needs/${needId}`, body);
  return data;
}

export async function deactivateContentNeed(needId: string): Promise<void> {
  await apiClient.delete(`/admin/ingress/needs/${needId}`);
}

// ============================================================================
// Entity Groups
// ============================================================================

export type EditorialGuarantee = 'all_sites' | 'sector_routed' | 'none';

export interface CoveragePolicy {
  cadence_minutes: number;
  sources: string[];
  priority: number;
  budget_exempt: boolean;
  editorial_guarantee: EditorialGuarantee;
}

export interface EntityGroup {
  group_id: string;
  group_name: string;
  description: string | null;
  group_type: string;
  dynamic_criteria: Record<string, any>;
  is_active: boolean;
  is_fast_lane?: boolean;
  coverage_policy?: CoveragePolicy | null;
  member_count?: number;
  created_at: string;
  updated_at: string;
}

export interface EntityGroupCreate {
  group_name: string;
  description?: string | null;
  group_type?: string;
  dynamic_criteria?: Record<string, any>;
  entity_ids?: string[];
}

export interface EntityGroupUpdate {
  group_name?: string;
  description?: string | null;
  dynamic_criteria?: Record<string, any>;
  is_active?: boolean;
  is_fast_lane?: boolean;
  coverage_policy?: CoveragePolicy | null;
}

export interface EntityGroupMember {
  entity_id: string;
  entity_type: string;
  canonical_identifier: string;
  display_name: string;
  added_at: string;
}

export interface ResolveResult {
  group_id: string;
  group_name: string;
  group_type: string;
  resolved_count: number;
  entity_ids: string[];
  entities: Array<{
    entity_id: string;
    canonical_identifier: string;
    display_name: string | null;
    entity_type: string;
  }>;
}

/**
 * The backend returns jsonb columns as raw asyncpg JSON strings (no jsonb
 * codec is registered server-side), so `coverage_policy` / `dynamic_criteria`
 * arrive as strings — not the objects the EntityGroup interface declares.
 * A string coverage_policy is truthy, so it slips past `policy && ...` guards
 * and then `policy.sources.join(...)` throws mid-render (white-screens the
 * Pipeline page). Normalise here so every consumer gets the declared shape.
 */
function parseGroupJson(raw: unknown): Record<string, any> | null {
  let v: any = raw;
  // Unwrap up to a few string layers (past double-encoding bugs).
  for (let i = 0; i < 4 && typeof v === 'string'; i++) {
    try { v = JSON.parse(v); } catch { return null; }
  }
  return v && typeof v === 'object' && !Array.isArray(v) ? v : null;
}

function normalizeGroup(group: EntityGroup): EntityGroup {
  return {
    ...group,
    dynamic_criteria: parseGroupJson(group.dynamic_criteria) ?? {},
    // null must stay null — the coverage checkbox keys off `!!coverage_policy`.
    coverage_policy: (parseGroupJson(group.coverage_policy) as CoveragePolicy | null),
  };
}

export async function listEntityGroups(activeOnly: boolean = true): Promise<EntityGroup[]> {
  const { data } = await apiClient.get('/admin/ingress/groups', {
    params: { active_only: activeOnly },
  });
  return (data.groups as EntityGroup[]).map(normalizeGroup);
}

export async function getEntityGroup(groupId: string): Promise<EntityGroup> {
  const { data } = await apiClient.get(`/admin/ingress/groups/${groupId}`);
  return normalizeGroup(data);
}

export async function createEntityGroup(body: EntityGroupCreate): Promise<EntityGroup> {
  const { data } = await apiClient.post('/admin/ingress/groups', body);
  return normalizeGroup(data);
}

export async function updateEntityGroup(groupId: string, body: EntityGroupUpdate): Promise<EntityGroup> {
  const { data } = await apiClient.put(`/admin/ingress/groups/${groupId}`, body);
  return normalizeGroup(data);
}

export async function getGroupMembers(groupId: string): Promise<EntityGroupMember[]> {
  const { data } = await apiClient.get(`/admin/ingress/groups/${groupId}/members`);
  return data.members;
}

export async function addGroupMembers(groupId: string, entityIds: string[]): Promise<{ added: number }> {
  const { data } = await apiClient.post(`/admin/ingress/groups/${groupId}/members`, entityIds);
  return data;
}

export async function removeGroupMember(groupId: string, entityId: string): Promise<void> {
  await apiClient.delete(`/admin/ingress/groups/${groupId}/members/${entityId}`);
}

export async function resolveGroup(groupId: string): Promise<ResolveResult> {
  const { data } = await apiClient.get(`/admin/ingress/groups/${groupId}/resolve`);
  return data;
}

// ============================================================================
// Priority Schedules
// ============================================================================

export interface PrioritySchedule {
  schedule_id: string;
  schedule_name: string;
  description: string | null;
  time_windows: Record<string, any>[];
  entity_tiers: Record<string, any>;
  content_type_boosts: Record<string, any>;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface PriorityScheduleCreate {
  schedule_name: string;
  description?: string | null;
  time_windows?: Record<string, any>[];
  entity_tiers?: Record<string, any>;
  content_type_boosts?: Record<string, any>;
}

export interface PriorityScheduleUpdate {
  schedule_name?: string;
  description?: string | null;
  time_windows?: Record<string, any>[];
  entity_tiers?: Record<string, any>;
  content_type_boosts?: Record<string, any>;
  is_active?: boolean;
}

export async function listPrioritySchedules(activeOnly: boolean = true): Promise<PrioritySchedule[]> {
  const { data } = await apiClient.get('/admin/ingress/priorities', {
    params: { active_only: activeOnly },
  });
  return data.schedules;
}

export async function getPrioritySchedule(scheduleId: string): Promise<PrioritySchedule> {
  const { data } = await apiClient.get(`/admin/ingress/priorities/${scheduleId}`);
  return data;
}

export async function createPrioritySchedule(body: PriorityScheduleCreate): Promise<PrioritySchedule> {
  const { data } = await apiClient.post('/admin/ingress/priorities', body);
  return data;
}

export async function updatePrioritySchedule(
  scheduleId: string,
  body: PriorityScheduleUpdate,
): Promise<PrioritySchedule> {
  const { data } = await apiClient.put(`/admin/ingress/priorities/${scheduleId}`, body);
  return data;
}

export async function deletePrioritySchedule(scheduleId: string): Promise<void> {
  await apiClient.delete(`/admin/ingress/priorities/${scheduleId}`);
}

// ============================================================================
// Ingress Actions
// ============================================================================

export async function triggerManualFetch(sourceName: string): Promise<{ status: string; job_id: string }> {
  const { data } = await apiClient.post(`/admin/ingress/sources/${sourceName}/fetch`);
  return data;
}

export interface IngestionStats {
  ingested_today: number;
  active_needs: number;
  fulfilled_today: number;
}

export async function getIngestionStats(): Promise<IngestionStats> {
  const { data } = await apiClient.get('/admin/ingress/stats');
  return data;
}

// ============================================================================
// Scheduler Config
// ============================================================================

export interface SchedulerConfig {
  is_enabled: boolean;
  poll_interval_minutes: number;
  /** When false, the auto_publish hooks in pipeline.py skip create_publish_job.
   *  Articles still progress to approved status; operator can publish later. */
  publishing_enabled?: boolean;
  /** When false, the publishing-service auto-promote hook skips with reason
   *  kill_switch_off. Manual POST /articles/{id}/promote is unaffected. */
  auto_promotion_enabled?: boolean;
}

/** v2 windowed schedule model. A monitor polls only inside its windows;
 *  any time not in a window = no polling. See
 *  MONITOR_SCHEDULING_OVERHAUL_PLAN.md. */
export interface ScheduleCadence {
  type: 'interval' | 'explicit';
  /** interval: minutes between fires, clock-anchored. */
  minutes?: number;
  /** interval: minute-of-hour the grid is offset to (default 0). */
  anchor_minute?: number;
  /** explicit: exact HH:MM fire times. */
  times?: string[];
}

export interface ScheduleWindow {
  label?: string;
  /** Weekday short names this window is active on: 'mon'..'sun'. */
  days: string[];
  /** HH:MM window open — interval cadence only. */
  start?: string;
  /** HH:MM window close — interval cadence only. */
  end?: string;
  cadence: ScheduleCadence;
}

export interface NeedScheduleConfig {
  version?: number;
  timezone?: string;
  windows?: ScheduleWindow[];
}

export interface NextFirePreview {
  timezone: string;
  is_v2: boolean;
  /** Pre-formatted "Tue 20 May 08:15" strings, soonest first. */
  upcoming_fires: string[];
  last_fetched_at?: string | null;
}

export interface PipelineHealth {
  config: SchedulerConfig;
  auto_promote_24h: {
    totals: { queued: number; skipped: number; enqueue_failed: number };
    by_reason: Record<string, Record<string, number>>;
  };
}

export async function getSchedulerConfig(): Promise<SchedulerConfig> {
  const { data } = await apiClient.get('/admin/ingress/scheduler-config');
  return data;
}

export async function updateSchedulerConfig(updates: Partial<SchedulerConfig>): Promise<SchedulerConfig> {
  const { data } = await apiClient.put('/admin/ingress/scheduler-config', updates);
  return data;
}

/** Next fire times for a SAVED need. */
export async function getNeedNextFire(needId: string): Promise<NextFirePreview> {
  const { data } = await apiClient.get(`/admin/ingress/needs/${needId}/next-fire`);
  return data;
}

/** Next fire times for an UNSAVED schedule_config — powers the live
 *  preview in the Edit Monitor modal against in-progress form state. */
export async function previewSchedule(config: NeedScheduleConfig): Promise<NextFirePreview> {
  const { data } = await apiClient.post('/admin/ingress/preview-schedule', config);
  return data;
}

export async function getPipelineHealth(): Promise<PipelineHealth> {
  const { data } = await apiClient.get('/admin/ingress/pipeline-health');
  return data;
}
